import { useState, useEffect } from 'react';
import {
    SearchLg,
    UserPlus01,
    Mail01,
    CheckCircle,
    XCircle,
    Edit01,
    Trash01,
    X
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { Button } from '@/components/base/buttons/button';
import { ModalOverlay, Modal, Dialog } from '@/components/application/modals/modal';
import { Checkbox } from '@/components/base/checkbox/checkbox';
import type { TeamRole, TeamMemberStatus } from '@/types/team';

// Extended team member type with permissions
interface TeamMemberWithPermissions {
    id: string;
    accountId: string;
    name: string;
    email: string;
    role: TeamRole;
    status: TeamMemberStatus;
    invitedAt: Date;
    lastLoginAt?: Date;
    canEdit: boolean;
    canDelete: boolean;
}
import { useTranslation } from 'react-i18next';
import { ShipperOnboardingHeaderAction } from '@/components/application/onboarding/shipper-onboarding-context';
import { getCompanyTeam, inviteTeamMember, updateTeamMember, removeTeamMember } from '@/data/api/companies';
import { useMe } from '@/hooks/api/use-auth';
import { toast } from '@/lib/toast-service';

// Helper function to map backend role to frontend role
const mapBackendRoleToFrontend = (backendRole: string): TeamRole => {
    const role = backendRole.toLowerCase();
    switch (role) {
        case 'company_admin':
        case 'super_admin':
            return 'Admin';
        case 'operator':
            return 'Editor';
        case 'viewer':
            return 'Viewer';
        default:
            return 'Viewer';
    }
};

// Helper function to map frontend role back to backend role
const mapFrontendRoleToBackend = (frontendRole: TeamRole): 'operator' | 'company_admin' => {
    switch (frontendRole) {
        case 'Admin':
            return 'company_admin';
        case 'Editor':
            return 'operator';
        case 'Viewer':
        default:
            return 'operator';
    }
};

type ShipperTeamPageProps = { embedded?: boolean };

export const ShipperTeamPage = ({ embedded = false }: ShipperTeamPageProps) => {
    const { t } = useTranslation(['pages:team', 'common']);
    const { data: currentUser, isLoading: isLoadingUser } = useMe();
    const [teamMembers, setTeamMembers] = useState<TeamMemberWithPermissions[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<TeamRole | ''>('');
    const [statusFilter, setStatusFilter] = useState<TeamMemberStatus | ''>('');

    // Add member modal state
    const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [newMemberFirstName, setNewMemberFirstName] = useState('');
    const [newMemberLastName, setNewMemberLastName] = useState('');
    const [newMemberRole, setNewMemberRole] = useState<'operator' | 'company_admin'>('operator');

    // Edit modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingMember, setEditingMember] = useState<TeamMemberWithPermissions | null>(null);
    const [editRole, setEditRole] = useState<'operator' | 'company_admin'>('operator');
    const [editIsActive, setEditIsActive] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);

    // Remove modal state
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [removingMember, setRemovingMember] = useState<TeamMemberWithPermissions | null>(null);
    const [sendNotificationEmail, setSendNotificationEmail] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    // Check if current user is admin
    const isAdmin = currentUser?.role?.toLowerCase() === 'company_admin' ||
                    currentUser?.role?.toLowerCase() === 'super_admin';

    const roleOptions = [
        { id: '', label: t('list.allRoles') },
        { id: 'Admin', label: 'Admin' },
        { id: 'Editor', label: 'Editor' },
        { id: 'Viewer', label: 'Viewer' }
    ];

    const statusOptions = [
        { id: '', label: t('list.allStatuses') },
        { id: 'Active', label: t('status.active', { ns: 'types' }) },
        { id: 'Inactive', label: t('status.inactive', { ns: 'types' }) }
    ];

    const roleFormOptions = [
        { id: 'operator', label: t('list.modal.roles.operator') },
        { id: 'company_admin', label: t('list.modal.roles.companyAdmin') },
    ];

    const loadTeamMembers = async () => {
        if (!currentUser?.company_id) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const apiMembers = await getCompanyTeam(currentUser.company_id);

            // Map API response to TeamMember format
            const mappedMembers: TeamMemberWithPermissions[] = apiMembers.map((member) => ({
                id: member.user_id,
                accountId: currentUser.company_id || '',
                name: member.full_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
                email: member.email,
                role: mapBackendRoleToFrontend(member.role),
                status: member.is_active ? 'Active' : 'Inactive' as TeamMemberStatus,
                invitedAt: new Date(member.created_at),
                lastLoginAt: member.last_login_at ? new Date(member.last_login_at) : undefined,
                canEdit: member.can_edit,
                canDelete: member.can_delete,
            }));

            setTeamMembers(mappedMembers);
        } catch (error) {
            console.error('Error loading team members:', error);
            toast.error(t('list.errorLoading', { ns: 'common' }) || 'Error loading team members');
            setTeamMembers([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isLoadingUser) {
            loadTeamMembers();
        }
    }, [currentUser?.company_id, isLoadingUser]);

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'Admin': return 'error';
            case 'Editor': return 'warning';
            case 'Viewer': return 'brand';
            default: return 'gray';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Inactive': return 'error';
            default: return 'gray';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Active': return CheckCircle;
            case 'Inactive': return XCircle;
            default: return XCircle;
        }
    };

    // Add member handler
    const handleAddMember = async () => {
        if (!currentUser?.company_id || !newMemberEmail.trim()) {
            toast.error(t('list.modal.error.emailRequired'));
            return;
        }

        setIsSubmitting(true);
        try {
            await inviteTeamMember(currentUser.company_id, {
                email: newMemberEmail.trim(),
                first_name: newMemberFirstName.trim() || undefined,
                last_name: newMemberLastName.trim() || undefined,
                role: newMemberRole,
            });

            toast.success(t('list.modal.success.memberInvited'));
            setIsAddMemberModalOpen(false);

            // Reset form
            setNewMemberEmail('');
            setNewMemberFirstName('');
            setNewMemberLastName('');
            setNewMemberRole('operator');

            // Reload team members
            await loadTeamMembers();
        } catch (error: any) {
            console.error('Error inviting team member:', error);
            toast.error(error.message || t('list.modal.error.invitingMember'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Edit handlers
    const handleEditClick = (member: TeamMemberWithPermissions) => {
        setEditingMember(member);
        setEditRole(mapFrontendRoleToBackend(member.role));
        setEditIsActive(member.status === 'Active');
        setIsEditModalOpen(true);
    };

    const handleUpdateMember = async () => {
        if (!currentUser?.company_id || !editingMember) return;

        setIsUpdating(true);
        try {
            await updateTeamMember(currentUser.company_id, editingMember.id, {
                role: editRole,
                is_active: editIsActive,
            });

            toast.success(t('list.modal.success.memberUpdated'));
            setIsEditModalOpen(false);
            setEditingMember(null);
            await loadTeamMembers();
        } catch (error: any) {
            console.error('Error updating team member:', error);
            toast.error(error.message || t('list.modal.error.updatingMember'));
        } finally {
            setIsUpdating(false);
        }
    };

    // Remove handlers
    const handleRemoveClick = (member: TeamMemberWithPermissions) => {
        setRemovingMember(member);
        setSendNotificationEmail(false);
        setIsRemoveModalOpen(true);
    };

    const handleRemoveMember = async () => {
        if (!currentUser?.company_id || !removingMember) return;

        setIsRemoving(true);
        try {
            await removeTeamMember(currentUser.company_id, removingMember.id, {
                send_notification_email: sendNotificationEmail,
            });

            toast.success(t('list.modal.success.memberRemoved'));
            setIsRemoveModalOpen(false);
            setRemovingMember(null);
            setSendNotificationEmail(false);
            await loadTeamMembers();
        } catch (error: any) {
            console.error('Error removing team member:', error);
            toast.error(error.message || t('list.modal.error.removingMember'));
        } finally {
            setIsRemoving(false);
        }
    };

    const filteredMembers = teamMembers.filter(member => {
        const matchesSearch = !searchTerm ||
            member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRole = !roleFilter || member.role === roleFilter;
        const matchesStatus = !statusFilter || member.status === statusFilter;

        return matchesSearch && matchesRole && matchesStatus;
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {!embedded ? (
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{t('list.title')}</h1>
                        <p className="text-tertiary mt-1">{t('list.subtitle')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <ShipperOnboardingHeaderAction />
                        {isAdmin && (
                            <Button
                                onClick={() => setIsAddMemberModalOpen(true)}
                                size="md"
                                color="primary"
                                iconLeading={UserPlus01}
                            >
                                {t('list.invite')}
                            </Button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex justify-end gap-2">
                    <ShipperOnboardingHeaderAction />
                    {isAdmin && (
                        <Button
                            onClick={() => setIsAddMemberModalOpen(true)}
                            size="md"
                            color="primary"
                            iconLeading={UserPlus01}
                        >
                            {t('list.invite')}
                        </Button>
                    )}
                </div>
            )}

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Input
                        placeholder={t('list.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.rolePlaceholder')}
                        items={roleOptions}
                        selectedKey={roleFilter}
                        onSelectionChange={(key) => setRoleFilter(key as TeamRole | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as TeamMemberStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Team Members Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('list.title')} (${filteredMembers.length})`}
                    description={t('list.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('list.headers.name')} isRowHeader />
                        <Table.Head label={t('list.headers.email')} />
                        <Table.Head label={t('list.headers.role')} />
                        <Table.Head label={t('list.headers.status')} />
                        <Table.Head label={t('list.headers.lastLogin')} />
                        <Table.Head label={t('list.headers.inviteDate')} />
                        <Table.Head label={t('list.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {filteredMembers.map((member) => {
                            const StatusIcon = getStatusIcon(member.status);
                            return (
                                <Table.Row key={member.id}>
                                    <Table.Cell>
                                        <div className="font-medium text-primary">{member.name}</div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <Mail01 className="size-4 text-tertiary" />
                                            <span className="text-sm text-tertiary">{member.email}</span>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color={getRoleColor(member.role)} size="sm">
                                            {member.role}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <StatusIcon className="size-4" />
                                            <Badge color={getStatusColor(member.status)} size="sm">
                                                {member.status === 'Active' ? t('list.statusLabels.active') : t('list.statusLabels.inactive')}
                                            </Badge>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">
                                            {member.lastLoginAt
                                                ? member.lastLoginAt.toLocaleDateString('it-IT')
                                                : t('list.never')
                                            }
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">
                                            {member.invitedAt.toLocaleDateString('it-IT')}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {(member.canEdit || member.canDelete) ? (
                                            <Dropdown.Root>
                                                <Dropdown.DotsButton />
                                                <Dropdown.Popover className="w-min">
                                                    <Dropdown.Menu>
                                                        {member.canEdit && (
                                                            <Dropdown.Item
                                                                icon={Edit01}
                                                                onAction={() => handleEditClick(member)}
                                                            >
                                                                {t('list.actions.edit')}
                                                            </Dropdown.Item>
                                                        )}
                                                        {member.canDelete && (
                                                            <Dropdown.Item
                                                                icon={Trash01}
                                                                onAction={() => handleRemoveClick(member)}
                                                                className="text-error-solid"
                                                            >
                                                                {t('list.actions.remove')}
                                                            </Dropdown.Item>
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown.Popover>
                                            </Dropdown.Root>
                                        ) : null}
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {filteredMembers.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <UserPlus01 className="size-12 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-primary mb-2">{t('list.noMembers.title')}</h3>
                        <p className="text-tertiary">
                            {searchTerm || roleFilter || statusFilter
                                ? t('list.noMembers.message')
                                : t('list.noMembers.emptyMessage')
                            }
                        </p>
                    </div>
                    {isAdmin && !searchTerm && !roleFilter && !statusFilter && (
                        <Button
                            onClick={() => setIsAddMemberModalOpen(true)}
                            size="md"
                            color="primary"
                            iconLeading={UserPlus01}
                        >
                            {t('list.noMembers.addFirst')}
                        </Button>
                    )}
                </div>
            )}

            {/* Add Member Modal */}
            <ModalOverlay isOpen={isAddMemberModalOpen} onOpenChange={(open) => !open && setIsAddMemberModalOpen(false)}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary w-full max-w-md">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-semibold text-primary">
                                    {t('list.modal.addTitle')}
                                </h2>
                                <button
                                    onClick={() => setIsAddMemberModalOpen(false)}
                                    className="text-tertiary hover:text-primary"
                                    aria-label={t('list.modal.cancel')}
                                >
                                    <X className="size-5" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <Input
                                    label={t('list.modal.fields.email')}
                                    placeholder={t('list.modal.fields.emailPlaceholder')}
                                    value={newMemberEmail}
                                    onChange={setNewMemberEmail}
                                    type="email"
                                    isRequired
                                    size="md"
                                />

                                <div className="grid grid-cols-2 gap-4">
                                    <Input
                                        label={t('list.modal.fields.firstName')}
                                        placeholder={t('list.modal.fields.firstNamePlaceholder')}
                                        value={newMemberFirstName}
                                        onChange={setNewMemberFirstName}
                                        size="md"
                                    />

                                    <Input
                                        label={t('list.modal.fields.lastName')}
                                        placeholder={t('list.modal.fields.lastNamePlaceholder')}
                                        value={newMemberLastName}
                                        onChange={setNewMemberLastName}
                                        size="md"
                                    />
                                </div>

                                <Select
                                    label={t('list.modal.fields.role')}
                                    items={roleFormOptions}
                                    selectedKey={newMemberRole}
                                    onSelectionChange={(key) => setNewMemberRole(key as 'operator' | 'company_admin')}
                                    size="md"
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <Button
                                    onClick={() => setIsAddMemberModalOpen(false)}
                                    color="secondary"
                                    size="md"
                                >
                                    {t('list.modal.cancel')}
                                </Button>
                                <Button
                                    onClick={handleAddMember}
                                    color="primary"
                                    size="md"
                                    isLoading={isSubmitting}
                                    disabled={!newMemberEmail.trim()}
                                >
                                    {t('list.modal.invite')}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>

            {/* Edit Member Modal */}
            <ModalOverlay isOpen={isEditModalOpen} onOpenChange={(open) => !open && setIsEditModalOpen(false)}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary w-full max-w-md">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-semibold text-primary">
                                    {t('list.modal.editTitle')}
                                </h2>
                                <button
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="text-tertiary hover:text-primary"
                                    aria-label={t('list.modal.cancel')}
                                >
                                    <X className="size-5" />
                                </button>
                            </div>

                            {editingMember && (
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm font-medium text-primary mb-1">
                                            {t('list.modal.fields.name')}
                                        </p>
                                        <p className="text-sm text-tertiary">
                                            {editingMember.name}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-sm font-medium text-primary mb-1">
                                            {t('list.modal.fields.email')}
                                        </p>
                                        <p className="text-sm text-tertiary">
                                            {editingMember.email}
                                        </p>
                                    </div>

                                    <Select
                                        label={t('list.modal.fields.role')}
                                        items={roleFormOptions}
                                        selectedKey={editRole}
                                        onSelectionChange={(key) => setEditRole(key as 'operator' | 'company_admin')}
                                        size="md"
                                    >
                                        {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                    </Select>

                                    <Checkbox
                                        isSelected={editIsActive}
                                        onChange={setEditIsActive}
                                        label={t('list.modal.fields.isActive')}
                                    />
                                </div>
                            )}

                            <div className="flex justify-end gap-3 mt-6">
                                <Button
                                    onClick={() => setIsEditModalOpen(false)}
                                    color="secondary"
                                    size="md"
                                    disabled={isUpdating}
                                >
                                    {t('list.modal.cancel')}
                                </Button>
                                <Button
                                    onClick={handleUpdateMember}
                                    color="primary"
                                    size="md"
                                    isLoading={isUpdating}
                                >
                                    {t('list.modal.save')}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>

            {/* Remove Member Confirmation Modal */}
            <ModalOverlay isOpen={isRemoveModalOpen} onOpenChange={(open) => !open && setIsRemoveModalOpen(false)}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary w-full max-w-md">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-semibold text-primary">
                                    {t('list.modal.removeTitle')}
                                </h2>
                                <button
                                    onClick={() => setIsRemoveModalOpen(false)}
                                    className="text-tertiary hover:text-primary"
                                    aria-label={t('list.modal.cancel')}
                                    disabled={isRemoving}
                                >
                                    <X className="size-5" />
                                </button>
                            </div>

                            {removingMember && (
                                <div className="space-y-4">
                                    <p className="text-sm text-tertiary">
                                        {t('list.modal.removeConfirmation', {
                                            name: removingMember.name,
                                            email: removingMember.email
                                        })}
                                    </p>

                                    <div className="p-4 bg-secondary rounded-lg">
                                        <Checkbox
                                            isSelected={sendNotificationEmail}
                                            onChange={setSendNotificationEmail}
                                            label={t('list.modal.sendNotificationEmail')}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 mt-6">
                                <Button
                                    onClick={() => setIsRemoveModalOpen(false)}
                                    color="secondary"
                                    size="md"
                                    disabled={isRemoving}
                                >
                                    {t('list.modal.cancel')}
                                </Button>
                                <Button
                                    onClick={handleRemoveMember}
                                    color="primary-destructive"
                                    size="md"
                                    isLoading={isRemoving}
                                >
                                    {t('list.modal.confirmRemove')}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>

        </div>
    );
};
